/**
 * ¿Este `deepLink` es de esta aplicación?
 *
 * `Notification.deepLink` es **una sola columna compartida por las cuatro
 * capas**. El comentario del esquema todavía dice «Deep link en la app (ej:
 * "/orders/123")» porque nació para el móvil; después los tres frontales
 * empezaron a escribir sus propias rutas en el mismo sitio y nada las
 * distingue:
 *
 *   `/orders/:id`                → app móvil (pedido del consumidor)
 *   `/wallet`, `/creator/payouts`→ app móvil
 *   `/store/:id/orders?orderId=` → este panel
 *   `/merchants?merchantId=`     → panel de 121Pass
 *
 * Una misma persona puede recibir de varios tipos: un `Staff` que además
 * use la app como cliente tiene **un solo `User`**, así que su bandeja
 * mezcla los avisos de sus compras con los de su trabajo. El panel navegaba
 * a cualquiera de ellos sin mirar, y las rutas ajenas caían en su propio
 * 404 — el usuario pulsaba y llegaba a una pantalla de error.
 *
 * Gemelo de `DASHBOARD-STORE-121PASS/src/lib/deepLink.ts`: el defecto es el
 * mismo en los dos paneles porque la columna es la misma.
 *
 * En vez de mantener a mano una lista de prefijos que envejecería mal, se
 * le pregunta a la tabla de rutas del propio panel. Si mañana se añade una
 * ruta, esto se entera solo.
 */

import { matchRoutes } from 'react-router-dom';
import { routes } from '@/router';

/**
 * Devuelve el enlace si esta aplicación sabe servirlo, o `null` si
 * pertenece a otra capa (o no es una ruta interna).
 *
 * Un `deepLink` que sólo casa con el comodín `*` cuenta como no
 * resoluble: ese comodín es precisamente la pantalla de «no encontrada».
 */
export function resolveDashboardLink(deepLink: string | null): string | null {
  if (!deepLink) return null;

  // Sólo rutas internas. Un `//host` o un `/\host` los interpretan algunos
  // navegadores como absolutos, así que fuera; el mismo criterio que
  // `sanitizeRedirect`.
  if (!deepLink.startsWith('/')) return null;
  if (deepLink.startsWith('//') || deepLink.startsWith('/\\')) return null;
  if (deepLink.includes('\\')) return null;

  const [pathname] = deepLink.split('?');
  const matches = matchRoutes(routes, pathname);
  if (!matches || matches.length === 0) return null;

  const leaf = matches[matches.length - 1]?.route;
  if (!leaf || leaf.path === '*') return null;

  return deepLink;
}
